import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderHeart, CheckCircle, Archive, AlertTriangle } from 'lucide-react';
import EmployeePortalCrudShell from './EmployeePortalCrudShell';
import {
  DOCUMENT_STATUS,
  DOCUMENT_CATEGORIES,
  statusBadge,
  defaultStats,
  normalizeDocumentCategory,
} from './employeePortalShared';
import { formatPortalDate, resolveOptionLabel } from './employeePortalSaudiFormat';
import { usePortalEmployeeContext } from './PortalEmployeeContext';

const NS = 'documentsPage';
const API = '/api/employee-portal/documents';

const EMPTY = {
  document_code: '', title: '', category: '', file_url: '', file_size: '', status: 'actif', expiry_date: '', notes: '',
};

export default function EmployeePortalDocumentsPage() {
  const { i18n } = useTranslation();
  const { employeeId, name } = usePortalEmployeeContext();
  const apiQueryParams = useMemo(() => (employeeId ? { employee_id: employeeId } : {}), [employeeId]);

  const catOpts = DOCUMENT_CATEGORIES.map((c) => ({ value: c.value, labelKey: c.labelKey }));
  const statusOpts = DOCUMENT_STATUS.map((s) => ({ value: s.value, labelKey: s.labelKey }));

  const renderCategory = (r, _p, _t, pl) => resolveOptionLabel(
    normalizeDocumentCategory(r.category),
    DOCUMENT_CATEGORIES,
    pl,
  );

  const renderExpiry = (r) => formatPortalDate(r.expiry_date, i18n.language);

  return (
    <EmployeePortalCrudShell
      ns={NS}
      icon={FolderHeart}
      apiPath={API}
      apiQueryParams={apiQueryParams}
      requireEmployeeScope
      codeField="document_code"
      entityPrefixKey="employeePortalDocument"
      codePrefixFallback="EPD"
      gradient="from-violet-500 to-violet-700"
      emptyForm={EMPTY}
      mapRecordToForm={(r) => ({
        document_code: r.document_code ?? '',
        title: r.title ?? '',
        category: normalizeDocumentCategory(r.category) ?? '',
        file_url: r.file_url ?? '',
        file_size: r.file_size ?? '',
        status: r.status ?? 'actif',
        expiry_date: r.expiry_date ?? '',
        notes: r.notes ?? '',
      })}
      mapFormToPayload={(f) => ({
        document_code: f.document_code || null,
        title: f.title,
        category: f.category || null,
        file_url: f.file_url || null,
        file_size: f.file_size || null,
        status: f.status || 'actif',
        expiry_date: f.expiry_date || null,
        notes: f.notes || null,
        employee_id: employeeId || null,
        employee_name: name || null,
      })}
      computeStats={(records) => defaultStats(records, {
        totalIcon: FolderHeart,
        fields: [
          { titleKey: 'stat_active', field: 'status', value: 'actif', icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { titleKey: 'stat_archived', field: 'status', value: 'archive', icon: Archive, color: 'from-gray-500 to-gray-600' },
          { titleKey: 'stat_expired', field: 'status', value: 'expire', icon: AlertTriangle, color: 'from-red-500 to-red-600' },
        ],
      })}
      columns={[
        { key: 'document_code', labelKey: 'col_code' },
        { key: 'title', labelKey: 'col_title' },
        { key: 'category', labelKey: 'col_category', render: renderCategory },
        { key: 'status', labelKey: 'col_status', render: (r, _p, _t, pl) => statusBadge(r.status, pl, DOCUMENT_STATUS) },
        { key: 'expiry_date', labelKey: 'col_expiry', render: renderExpiry },
      ]}
      formSections={[
        {
          id: 'document',
          titleKey: 'section_document',
          fields: [
            { key: 'title', labelKey: 'field_title', required: true },
            { key: 'category', labelKey: 'field_category', type: 'select', options: catOpts },
            { key: 'status', labelKey: 'field_status', type: 'select', options: statusOpts },
            { key: 'expiry_date', labelKey: 'field_expiry', type: 'date' },
            { key: 'file_size', labelKey: 'field_size' },
          ],
        },
        {
          id: 'file',
          titleKey: 'section_file',
          fields: [
            { key: 'file_url', labelKey: 'field_url', fullWidth: true },
            { key: 'notes', labelKey: 'field_notes', type: 'textarea', fullWidth: true },
          ],
        },
      ]}
    />
  );
}
